import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import {
  enrollMfa,
  getEnrolledMfaPhone,
  getMfaStatus,
  initRecaptchaVerifier,
  sendMfaVerification,
  unenrollMfa,
} from '../../services/authService';
import ConfirmationDialog from '../Dialogs/ConfirmationDialog';

const mapMfaError = (error: { code?: string; message?: string }): string => {
  if (error.code === 'auth/requires-recent-login') {
    return 'This is a sensitive operation and requires a recent login. Please log out and sign in again to enable MFA.';
  }
  if (error.code === 'auth/operation-not-allowed') {
    return 'Phone authentication is not enabled. Please contact support.';
  }
  if (error.message?.includes('ADMIN_ONLY_OPERATION')) {
    return 'Phone verification is not properly configured. Please contact support.';
  }
  return `Failed to send verification code: ${error.message || 'Unknown error'}`;
};

interface MfaManagementPanelProps {
  onMfaStatusChange?: (enabled: boolean) => void;
  elevation?: number;
}

const MfaManagementPanel: React.FC<MfaManagementPanelProps> = ({
  onMfaStatusChange = () => {
    // No-op default callback
  },
  elevation = 3,
}) => {
  const { account } = useAccount();
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [enrolledPhone, setEnrolledPhone] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('main');
  const [isDisenrollDialogOpen, setDisenrollDialogOpen] = useState(false);

  const recaptchaVerifierRef = useRef<unknown>(null);

  const getRecaptchaVerifier = async () => {
    if (!recaptchaVerifierRef.current) {
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
      recaptchaVerifierRef.current = await initRecaptchaVerifier('recaptcha-container');
    }
    return recaptchaVerifierRef.current;
  };

  useEffect(() => {
    getMfaStatus().then((mfaEnrolled) => {
      setIsMfaEnabled(mfaEnrolled);
      onMfaStatusChange(mfaEnrolled);
      if (mfaEnrolled) {
        getEnrolledMfaPhone().then((phone) => setEnrolledPhone(phone || null));
      }
    });
  }, [onMfaStatusChange]);

  useEffect(() => {
    // Cleanup recaptchaVerifier on component unmount
    return () => {
      if (recaptchaVerifierRef.current) {
        // biome-ignore lint/suspicious/noExplicitAny: Required for reCAPTCHA clear
        (recaptchaVerifierRef.current as any).clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const handleSendVerificationCode = async () => {
    // Guard against duplicate calls
    if (isLoading) {
      return;
    }

    setError('');
    setSuccess('');
    setIsLoading(true);
    setLoadingProgress(0);

    // Animate progress over ~10 seconds (typical SMS send time)
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2; // Increment by 2% every 200ms = 10 seconds total
      });
    }, 200);

    try {
      const verifier = await getRecaptchaVerifier();
      // biome-ignore lint/suspicious/noExplicitAny: UI decoupling prevents importing ApplicationVerifier
      const newVerificationId = await sendMfaVerification(verifier as any);

      clearInterval(progressInterval);
      setLoadingProgress(100);
      setVerificationId(newVerificationId);
      setSuccess('A verification code has been sent to your phone.');
      setView('verify');
    } catch (err: unknown) {
      clearInterval(progressInterval);
      const error = err as { code?: string; message?: string };
      setError(mapMfaError(error));
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
    }
  };

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (!verificationId) {
        setError('Verification ID not available');
        return;
      }
      await enrollMfa(verificationId, verificationCode);

      setIsMfaEnabled(true);
      onMfaStatusChange(true);
      setEnrolledPhone(account?.phoneNumber || null);
      setSuccess('Multi-Factor Authentication enabled successfully!');
      setView('main');
      setVerificationCode('');
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(`Failed to verify code: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisenroll = async () => {
    setDisenrollDialogOpen(false);
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await unenrollMfa();

      setIsMfaEnabled(false);
      onMfaStatusChange(false);
      setEnrolledPhone(null);
      setSuccess('Multi-Factor Authentication has been disabled.');
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      if (error.code === 'auth/requires-recent-login') {
        setError(
          'This is a sensitive operation and requires a recent login. Please log out and sign in again to disable MFA.'
        );
      } else {
        setError(`Failed to disable MFA: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderMainView = () => (
    <Box>
      {isMfaEnabled ? (
        <Box>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            MFA is currently <strong>enabled</strong> on your account.
          </Typography>
          <Typography variant="body2" color="text.primary" gutterBottom sx={{ mb: 2 }}>
            Enrolled factor: Phone ending in ...{enrolledPhone?.slice(-4) || 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
            Note: Clicking this button will disable Multi-Factor Authentication on your account.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDisenrollDialogOpen(true)}
            disabled={isLoading}
            size="large"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              py: 1.5,
              px: 3,
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Disable MFA'}
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            MFA is currently <strong>disabled</strong>. Add an extra layer of security to your
            account.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
            Note: Clicking this button will send an SMS verification code to your phone number.
          </Typography>
          <Button
            variant="contained"
            onClick={handleSendVerificationCode}
            disabled={isLoading}
            size="large"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              py: 1.5,
              px: 3,
            }}
          >
            {isLoading ? (
              <CircularProgress
                variant="determinate"
                value={loadingProgress}
                size={24}
                sx={{ color: 'white' }}
              />
            ) : (
              'Enable MFA'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderVerifyView = () => (
    <Box component="form" onSubmit={handleVerify}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Verify Your Phone
      </Typography>
      <Typography variant="body1" color="text.primary" gutterBottom sx={{ mb: 3 }}>
        Enter the 6-digit code sent to your phone.
      </Typography>
      <TextField
        label="Verification Code"
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value.trim())}
        fullWidth
        required
        margin="normal"
        sx={{ mb: 3 }}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          size="large"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 600,
            py: 1.5,
            px: 3,
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Enable MFA'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => setView('enroll')}
          disabled={isLoading}
          size="large"
          sx={{
            fontSize: '1rem',
            py: 1.5,
            px: 3,
          }}
        >
          Back
        </Button>
      </Box>
    </Box>
  );

  return (
    <Paper elevation={elevation} sx={{ padding: 3, height: '100%' }}>
      <Typography variant="h5" gutterBottom>
        Multi-Factor Authentication (MFA)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {view === 'main' && renderMainView()}
      {view === 'verify' && renderVerifyView()}

      <div id="recaptcha-container" />

      <ConfirmationDialog
        open={isDisenrollDialogOpen}
        onClose={() => setDisenrollDialogOpen(false)}
        onConfirm={handleDisenroll}
        title="Confirm MFA Disablement"
        contentText="Are you sure you want to disable Multi-Factor Authentication? This will reduce the security of your account."
      />
    </Paper>
  );
};

export default MfaManagementPanel;
